// @flow
// eslint-disable ta-code-conventions/has-matching-storydoc
import * as React from 'react';
import { VisibilityObserver } from '@ta/common.is-visible';

type Props = {|
  /** A number to specify a pixel-count fuzz-factor for determining visibility */
  threshold: number,

  /** Should we still keep loading */
  hasMore: boolean,

  /** Callback function to load the next page */
  loadMore: () => void,

  /** After loading more, check if the trigger node is still visible or not after the given delay.
   * Defaults to 1000ms */
  checkVisibilityDelay: number,

  /** normal react children */
  children: React.Node,

  /** Pass the raw target element ref in directly. When this element becomes visible, try to load more */
  triggerNode: { current: HTMLElement | null },
|};

const InfiniteLoaderObserver = ({
  threshold,
  hasMore,
  loadMore,
  checkVisibilityDelay,
  children,
  triggerNode,
}: Props) => {
  const [visible, setVisible] = React.useState(false);

  const maybeLoadMore = React.useCallback(() => {
    if (hasMore) {
      loadMore();
    }

    setVisible(true);
  }, [hasMore, loadMore]);

  // try to load more if the trigger element is still visible even after loading more
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      if (visible && hasMore) {
        maybeLoadMore();
      }
    }, checkVisibilityDelay);
    return () => clearTimeout(timeout);
  }, [visible, hasMore, maybeLoadMore, checkVisibilityDelay]);

  const setInvisible = React.useCallback(() => setVisible(false), []);

  return (
    <VisibilityObserver
      rootMargin={`${threshold}px`}
      onVisible={maybeLoadMore}
      onInvisible={setInvisible}
      node={triggerNode}
    >
      {children}
    </VisibilityObserver>
  );
};

InfiniteLoaderObserver.defaultProps = {
  checkVisibilityDelay: 1000,
};

export default InfiniteLoaderObserver;
